import React, { useState, useEffect } from 'react';
import { submitTestimonial, getCategories } from '../config/api';
import StarRating from './StarRating';

export default function TestimonialForm() {
	const [form, setForm] = useState({
		authorName: '',
		email: '',
		rating: 0,
		message: '',
		categoryId: '',
	});
	const [errors, setErrors] = useState({});
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const [categories, setCategories] = useState([]);

	useEffect(() => {
		loadCategories();
	}, []);

	async function loadCategories() {
		try {
			const res = await getCategories();
			setCategories(res.data?.data || []);
		} catch (err) {
			console.error('Failed to load categories:', err);
		}
	}

	function set(key, value) {
		setForm(f => ({ ...f, [key]: value }));
		setErrors(e => ({ ...e, [key]: undefined }));
	}

	function validate() {
		const errs = {};
		if (!form.authorName || form.authorName.trim().length < 2 || form.authorName.trim().length > 100) {
			errs.authorName = 'Name must be 2-100 characters';
		}
		if (!form.email || !form.email.includes('@')) {
			errs.email = 'Please enter a valid email';
		}
		if (!form.rating || form.rating < 1 || form.rating > 5) {
			errs.rating = 'Please select a rating';
		}
		if (!form.message || form.message.trim().length < 10 || form.message.trim().length > 500) {
			errs.message = 'Message must be 10-500 characters';
		}
		return errs;
	}

	async function handleSubmit(e) {
		e.preventDefault();
		const errs = validate();
		if (Object.keys(errs).length) {
			setErrors(errs);
			return;
		}
		setLoading(true);
		setErrors({});
		setSuccess(false);
		try {
			await submitTestimonial({
				author_name: form.authorName.trim(),
				author_email: form.email.trim(),
				rating: form.rating,
				message: form.message.trim(),
				photo_category_id: form.categoryId || null,
			});
			setSuccess(true);
			setForm({ authorName: '', email: '', rating: 0, message: '', categoryId: '' });
			// Auto-hide success message after 6 seconds
			setTimeout(() => setSuccess(false), 6000);
		} catch (err) {
			console.error('Testimonial submission error:', err);
			setErrors({ submit: 'Something went wrong. Please try again.' });
		} finally {
			setLoading(false);
		}
	}

	function inputStyle(hasError) {
		return {
			border: 'none',
			borderBottom: '1px solid ' + (hasError ? 'var(--color-error)' : 'var(--color-border)'),
			background: 'transparent',
			padding: '9px 0',
			fontSize: 13,
			color: 'var(--color-text)',
			outline: 'none',
			fontFamily: 'var(--font-body)',
			width: '100%',
		};
	}

	const labelStyle = {
		fontSize: 10,
		letterSpacing: '0.12em',
		textTransform: 'uppercase',
		color: 'var(--color-muted)',
		display: 'block',
		marginBottom: 7,
		fontFamily: 'var(--font-body)',
	};

	const errStyle = {
		fontSize: 11,
		color: 'var(--color-error)',
		marginTop: 4,
		fontFamily: 'var(--font-body)',
	};

	return (
		<form onSubmit={handleSubmit} noValidate>
			{success && (
				<div style={{ background: '#f0fff4', borderLeft: '3px solid var(--color-success)', padding: '14px 16px', fontSize: 13, color: '#1a5c34', marginBottom: 24, fontFamily: 'var(--font-body)' }}>
					Thank you! Your testimonial is now live.
				</div>
			)}
			{errors.submit && (
				<div style={{ background: '#fff5f5', borderLeft: '3px solid var(--color-error)', padding: '14px 16px', fontSize: 13, color: '#7f1d1d', marginBottom: 24, fontFamily: 'var(--font-body)' }}>
					{errors.submit}
				</div>
			)}

			<div>
				<label style={labelStyle}>YOUR NAME *</label>
				<input
					type="text"
					value={form.authorName}
					onChange={e => set('authorName', e.target.value)}
					style={inputStyle(errors.authorName)}
					placeholder="John Doe"
				/>
				{errors.authorName && <p style={errStyle}>{errors.authorName}</p>}
			</div>

			<div>
				<label style={labelStyle}>EMAIL ADDRESS *</label>
				<input
					type="email"
					value={form.email}
					onChange={e => set('email', e.target.value)}
					style={inputStyle(errors.email)}
					placeholder="john@example.com"
				/>
				{errors.email && <p style={errStyle}>{errors.email}</p>}
				<p style={{ fontSize: 11, color: 'var(--color-muted)', marginTop: 4, fontFamily: 'var(--font-body)' }}>
					Your email will not be displayed publicly
				</p>
			</div>

			<div>
				<label style={labelStyle}>YOUR RATING *</label>
				<div style={{ marginTop: 8, marginBottom: 8 }}>
					<StarRating value={form.rating} onChange={rating => set('rating', rating)} size={20} />
				</div>
				{errors.rating && <p style={errStyle}>{errors.rating}</p>}
			</div>

			<div>
				<label style={labelStyle}>PHOTO CATEGORY (OPTIONAL)</label>
				<select
					value={form.categoryId}
					onChange={e => set('categoryId', e.target.value)}
					style={{
						...inputStyle(false),
						background: 'var(--color-bg)',
						color: 'var(--color-text)',
						cursor: 'pointer'
					}}
				>
					<option value="" style={{ background: 'var(--color-bg)', color: 'var(--color-text)' }}>None</option>
					{categories.map(cat => (
						<option key={cat.id} value={cat.id} style={{ background: 'var(--color-bg)', color: 'var(--color-text)' }}>{cat.name}</option>
					))}
				</select>
			</div>

			<div>
				<label style={labelStyle}>YOUR TESTIMONIAL *</label>
				<textarea
					value={form.message}
					maxLength={500}
					onChange={e => set('message', e.target.value)}
					style={{ ...inputStyle(errors.message), resize: 'vertical', height: 100 }}
					placeholder="Share your experience with the photography session..."
				/>
				<div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
					{errors.message ? <p style={errStyle}>{errors.message}</p> : <span></span>}
					<span style={{ fontSize: 10, color: 'var(--color-muted)', fontFamily: 'var(--font-body)' }}>
						{form.message.length}/500
					</span>
				</div>
			</div>

			<button
				type="submit"
				disabled={loading}
				style={{
					width: '100%',
					padding: 14,
					background: loading ? 'var(--color-muted)' : 'var(--color-accent)',
					color: '#fff',
					border: 'none',
					fontSize: 11,
					letterSpacing: '0.1em',
					textTransform: 'uppercase',
					cursor: loading ? 'not-allowed' : 'pointer',
					fontFamily: 'var(--font-body)',
					marginTop: 8,
					borderRadius: 0,
				}}
			>
				{loading ? 'Submitting...' : 'Submit Testimonial →'}
			</button>
		</form>
	);
}
